import { useState } from "react";
import { Link, useNavigate } from "../lib/router";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { useMeta } from "../hooks/useMeta";
import { PageHeader } from "../components/PageHeader";
import { Overline, Reveal } from "../components/motion";
import { Field, PButton, TextArea } from "../components/forms";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT_EMAIL = "office@pilateson.de";
const WEB3FORMS_KEY = "7146e56a-6101-43bb-8c6b-54023812090a";

export default function Contact() {
  const { t, lang } = useLanguage();
  useMeta(t.seo.contact[0], t.seo.contact[1]);
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const rows = [
    [t.contact.emailLabel, CONTACT_EMAIL, "contact-email"],
    [t.contact.address, t.contact.addressValue, "contact-address"],
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(`${t.common.required}: ${t.contact.name}`);
      return;
    }
    if (!EMAIL_RE.test(form.email)) {
      toast.error(t.common.invalidEmail);
      return;
    }
    setSending(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: "Neue Kontaktanfrage – PILATES ON.",
          from_name: "PILATES ON. Website",
          name: form.name,
          email: form.email,
          phone: form.phone || "–",
          company: form.company || "–",
          message: form.message || "–",
          language: lang,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error("Form submission failed");
      navigate("/danke");
    } catch {
      toast.error(t.common.formError);
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader id="contact" overline={t.contact.overline} lines={t.contact.title} sub={t.contact.sub} />
      <section className="mx-auto max-w-[1400px] px-6 pb-32 md:px-12 lg:px-20">
        <div className="grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} className="grid gap-y-10" noValidate>
              <Field label={t.contact.name} id="contact-name" value={form.name} onChange={set("name")} required />
              <div className="grid gap-x-12 gap-y-10 md:grid-cols-2">
                <Field label={t.contact.email} id="contact-email-input" type="email" value={form.email} onChange={set("email")} required />
                <Field label={t.contact.phone} id="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} />
              </div>
              <Field label={t.contact.company} id="contact-company" value={form.company} onChange={set("company")} />
              <TextArea label={t.contact.message} id="contact-message" value={form.message} onChange={set("message")} />
              <div>
                <PButton type="submit" variant="solid" arrow={false} disabled={sending} data-testid="contact-form-submit">
                  {sending ? t.common.sending : t.contact.submit}
                </PButton>
                <p className="mt-4 max-w-2xl text-xs font-light leading-relaxed text-[#66625A]">Mit dem Absenden werden deine Angaben zur Bearbeitung deiner Anfrage verarbeitet. Weitere Informationen findest du in unserer <Link to="/datenschutz" className="underline underline-offset-2 transition-colors hover:text-[#C5A059]">Datenschutzerklärung</Link>.</p>
              </div>
            </form>
          </Reveal>
          <Reveal delay={0.2} className="lg:col-span-4 lg:col-start-9">
            <div data-testid="contact-info" className="bg-[#1C1C1A] p-10 text-[#FAF8F5] md:p-12">
              <Overline light className="mb-10">{t.contact.infoTitle}</Overline>
              <ul className="space-y-8">
                {rows.map(([label, value, id]) => (
                  <li key={id}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FAF8F5]/50">{label}</p>
                    <p data-testid={id} className="mt-2 whitespace-pre-line text-[15px] font-light text-[#FAF8F5]">
                      {id === "contact-email" ? <a href={`mailto:${value}`} className="transition-colors duration-300 hover:text-[#C5A059]">{value}</a> : value}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
